import { useEffect, useState } from 'react';
import { Button, Form, InputGroup } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import searchIcon from '../../assets/SidebarIcons/icon-search.svg';
import CustomAlert from '../../components/CustomAlert';
import config from '../../config/Constant';
import { getRequest } from '../../helpers/apiHandler';
import WhatsAppPricingRatesCreateModal from './WhatsAppPricingRatesCreateModal';
import WhatsAppPricingRatesUpdateModal from './WhatsAppPricingRatesUpdateModal';
import WhatsAppPricingRatesUploadModal from './WhatsAppPricingRatesUploadModal';

const categoryOptions = [
  { value: '', label: 'All Categories' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'utility', label: 'Utility' },
  { value: 'authentication', label: 'Authentication' },
];

const WhatsAppPricingRates = () => {
  const { access_token } = useSelector((state) => state.auth.user);
  const [loadingData, setLoadingData] = useState(false);
  const [search, setSearch] = useState('');
  const [data, setData] = useState([]);
  const [searchData, setSearchData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState(categoryOptions[0]);

  const [selectedData, setSelectedData] = useState({});
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const [alertState, setAlertState] = useState({
    show: false,
    variant: 'danger',
    message: '',
  });

  const getPricingRatesData = () => {
    setLoadingData(true);
    const requestData = { headers: { Authorization: access_token.token } };
    getRequest(`/v2/admin/whatsapp/pricing-rates?is_active=true`, requestData, (res) => {
      if (res.success) {
        const result = res.data || [];
        setData(result);
        setSearchData(result);
        generateColumns(result);
      } else {
        setAlertState({
          message: 'There was an issue while fetching WhatsApp pricing rates',
          show: true,
          variant: 'danger',
        });
      }
      setLoadingData(false);
    });
  };

  const generateColumns = (data) => {
    if (data.length > 0) {
      const cols = [
        {
          name: 'Country Code',
          sortable: true,
          selector: (row) => row.country_code,
          width: '120px',
        },
        { name: 'Country', sortable: true, selector: (row) => row.country_name },
        {
          name: 'Category',
          sortable: true,
          selector: (row) => row.pricing_category,
          cell: (row) => (
            <span style={{ textTransform: 'capitalize' }}>{row.pricing_category}</span>
          ),
          width: '130px',
        },
        {
          name: 'Cost/Conversation (USD)',
          sortable: true,
          selector: (row) => parseFloat(row.cost_per_conversation).toFixed(4),
          width: '180px',
        },
        {
          name: 'Effective From',
          sortable: true,
          selector: (row) => new Date(row.effective_from).toLocaleDateString(),
          width: '140px',
        },
        {
          name: 'Actions',
          selector: (row) => (
            <Button variant="primary" size="sm" onClick={() => openModal(row)}>
              Edit
            </Button>
          ),
          width: '100px',
        },
      ];
      setColumns(cols);
    }
  };

  const openModal = (rowData) => {
    setSelectedData(rowData);
    setShowUpdateModal(true);
  };

  const onSearch = (evt) => {
    const { value } = evt.target;
    filterData(value, categoryFilter);
    setSearch(value);
  };

  const onCategoryFilter = (selectedOption) => {
    const value = selectedOption?.value || '';
    filterData(search, value);
    setCategoryFilter(selectedOption);
  };

  const filterData = (searchValue, category) => {
    let filtered = data;

    if (searchValue) {
      filtered = filtered.filter(
        (item) =>
          item.country_name?.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.country_code?.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    if (category) {
      filtered = filtered.filter((item) => item.pricing_category === category);
    }

    setSearchData(filtered);
  };

  const selectStyles = {
    control: (base) => ({
      ...base,
      minWidth: 160,
      borderColor: '#e0e0e0',
      '&:hover': { borderColor: '#3b82f6' },
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#e0e7ff' : 'white',
      color: state.isSelected ? 'white' : '#333',
    }),
  };

  useEffect(() => {
    getPricingRatesData();
  }, []);

  useEffect(() => {
    if (alertState.show) {
      setTimeout(() => {
        setAlertState({ message: '', show: false, variant: '' });
      }, config.notify_time);
    }
  }, [alertState]);

  return (
    <div className="w-100">
      <div className="page-heading admin-heading">
        <div className="container-left" style={{ marginBottom: '32px' }}>
          <div className="page-title">WhatsApp Pricing Rates</div>
        </div>
        <div className="container-right mt-0 d-flex align-items-center gap-2">
          <Select
            value={categoryFilter}
            onChange={onCategoryFilter}
            options={categoryOptions}
            styles={selectStyles}
            isSearchable={false}
            placeholder="All Categories"
          />
          <InputGroup className="datatable-search search-input">
            <InputGroup.Append>
              <InputGroup.Text>
                <img alt="search" src={searchIcon} />
              </InputGroup.Text>
            </InputGroup.Append>
            <Form.Control
              placeholder="Search country..."
              name="search"
              value={search}
              onChange={onSearch}
              className="search-form"
            />
          </InputGroup>
          <Button
            variant="outline-primary"
            onClick={() => setShowUploadModal(true)}
            style={{ minWidth: 120, whiteSpace: 'nowrap' }}
          >
            Upload CSV
          </Button>
          <Button
            variant="primary"
            onClick={() => setShowCreateModal(true)}
            style={{ minWidth: 160, whiteSpace: 'nowrap' }}
          >
            Add New Rate
          </Button>
        </div>
      </div>
      <CustomAlert
        message={alertState.message}
        show={alertState.show}
        variant={alertState.variant}
        closeAlert={() => setAlertState({ show: false, variant: 'danger', message: '' })}
      />
      <DataTable
        columns={columns}
        data={searchData}
        progressPending={loadingData}
        paginationComponentOptions={{ rowsPerPageText: 'Items per page:' }}
        pagination
        paginationPerPage={20}
        paginationRowsPerPageOptions={[10, 20, 50, 100]}
      />
      <WhatsAppPricingRatesUpdateModal
        show={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
        rowData={selectedData}
        getPricingRatesData={getPricingRatesData}
      />
      <WhatsAppPricingRatesCreateModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        getPricingRatesData={getPricingRatesData}
      />
      <WhatsAppPricingRatesUploadModal
        show={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        getPricingRatesData={getPricingRatesData}
      />
    </div>
  );
};

export default WhatsAppPricingRates;
